import { useEffect } from "react";

import { Box, Container, Heading, SimpleGrid, VStack, useDisclosure } from "@chakra-ui/react";
import { useUser } from "@clerk/clerk-react";

import GroupUsageDialog from "~/components/groups/GroupUsageDialog";
import QuickAccessCard from "~/components/health-metrics/QuickAccessCard";
import MembersCard from "~/components/members/MembersCard";
import { useAppState } from "~/hooks/useAppState";
import Meta from "~/layout/Meta";
import { useAppStore } from "~/store";

// Special component just for the secret dialog
const SecretGroupDialog = ({ isOpen, onClose, userId }: { isOpen: boolean; onClose: () => void; userId: number }) => {
  // Custom handler for dialog closure
  const handleClose = () => {
    onClose();
  };

  return (
    <GroupUsageDialog
      isOpen={isOpen}
      onClose={handleClose}
      userId={userId}
      // Override props for the secret keyboard shortcut functionality only
      descriptionCallback={(option: string) => `User will use Henophess for: ${option}`}
      // Suppress the toast message
      suppressToast={true}
    />
  );
};

const HomePage = () => {
  const { userInfo } = useAppState("userInfo");
  const debugMode = useAppStore((state) => state.debugMode);
  const { isSignedIn, isLoaded } = useUser();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Add keyboard event handler for the secret key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if the key is pressed while any input element is focused
      if (["INPUT", "TEXTAREA", "SELECT"].includes((document.activeElement?.tagName || "").toUpperCase())) {
        return;
      }

      // Only trigger if 'g' is pressed and user is signed in
      if (event.key === "g" && isSignedIn && userInfo) {
        onOpen();
      }
    };

    // Add the event listener
    window.addEventListener("keydown", handleKeyDown);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isSignedIn, userInfo, onOpen]);

  return (
    <>
      <Meta title="Home" />

      <Container maxW="container.xl" py={4}>
        <VStack w="full" alignItems="stretch" spacing={6}>
          <Box>
            <Heading mb={4}>Welcome, {userInfo?.emailAddress}</Heading>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} alignItems="flex-start">
            {/* Members Card - Now left justified */}
            <Box>
              <MembersCard
                key={`members-card-${isSignedIn ? "signed-in" : "signed-out"}-${userInfo?.id || "no-user"}`}
              />
            </Box>

            {/* Quick Access Card for Health Metrics */}
            <Box>
              <QuickAccessCard />
            </Box>

            {/* Additional components can be added here */}
          </SimpleGrid>
        </VStack>
      </Container>

      {/* Secret Group Usage Dialog - Only shown when 'g' key is pressed */}
      {isSignedIn && userInfo && <SecretGroupDialog isOpen={isOpen} onClose={onClose} userId={userInfo.id} />}
    </>
  );
};

export default HomePage;
